/**
 * Mock Purchase Flow Service
 *
 * Simulates the full 9-stage agentic PO flow with mock data.
 * All mock data is loaded from __mocks__/purchaseFlowFixtures.ts.
 *
 * Two modes:
 *   1. Interactive: create a flow and chat through stages (default)
 *   2. Fixture preview: loadFixture('3_price_confirm') pre-loads a specific stage
 */

import type {
  PurchaseFlow,
  FlowListResponse,
  AgentToken,
  PurchaseStage,
  ChatMessage,
  ActivityEntry,
} from '@/types/purchaseFlow';
import { AgentTokenType, STAGE_ORDER } from '@/types/purchaseFlow';
import {
  VENDORS,
  PRODUCTS,
  PO_SUMMARY,
  INBOUND_SUMMARY,
  INVOICE_SUMMARY,
  MATCH_RESULT,
  FLOW_CHECKLIST,
  ALL_FIXTURES,
  DELIVERY_DATE,
  PAID_DATE,
} from './__mocks__/purchaseFlowFixtures';
import type { MockVendor, MockProduct } from '@/types/purchaseFlow';
import type { StageFixture } from './__mocks__/purchaseFlowFixtures';

// Re-export for consumers
export type { StageFixture };

// ============================================================================
// State
// ============================================================================

let flowCounter = 0;
const mockFlows = new Map<string, PurchaseFlow>();
const flowMessages = new Map<string, ChatMessage[]>();
const flowActivity = new Map<string, ActivityEntry[]>();

// ============================================================================
// Fixture API — load a pre-built stage for UI preview
// ============================================================================

/**
 * Load a fixture by key (e.g., '3_price_confirm').
 * Returns the fixture's flow, messages, and activity so the UI can render them.
 * Also registers the flow in the mock store so interactive chat continues from that point.
 */
export function loadFixture(key: string): StageFixture | null {
  const fixture = ALL_FIXTURES[key];
  if (!fixture) return null;

  const flow = { ...fixture.flow };
  mockFlows.set(flow.flow_id, flow);
  flowMessages.set(flow.flow_id, [...fixture.messages]);
  flowActivity.set(flow.flow_id, [...fixture.activity]);
  return fixture;
}

/** Get available fixture keys and labels */
export { ALL_FIXTURES, FIXTURE_LABELS } from './__mocks__/purchaseFlowFixtures';

/** Get pre-loaded messages for a flow (from fixture or accumulated during chat) */
export function getFlowMessages(flowId: string): ChatMessage[] {
  return flowMessages.get(flowId) || [];
}

/** Get pre-loaded activity for a flow */
export function getFlowActivity(flowId: string): ActivityEntry[] {
  return flowActivity.get(flowId) || [];
}

// ============================================================================
// Mock Flow CRUD
// ============================================================================

function createMockFlow(flowButtonId?: string): PurchaseFlow {
  const id = `mock-flow-${++flowCounter}`;
  const now = new Date().toISOString();
  const flow: PurchaseFlow = {
    flow_id: id,
    flow_button_id: flowButtonId,
    current_stage: 'vendor_select',
    stage_data: {},
    stage_history: [],
    status: 'active',
    created_by: 'demo-user',
    created_at: now,
    updated_at: now,
  };
  mockFlows.set(id, flow);
  return flow;
}

function advanceMockFlow(flowId: string, stage: PurchaseStage, data: Record<string, unknown>): PurchaseFlow {
  const flow = mockFlows.get(flowId);
  if (!flow) throw new Error('Flow not found');
  const prevStage = flow.current_stage;
  flow.stage_data = { ...flow.stage_data, ...data };
  flow.current_stage = stage;
  flow.updated_at = new Date().toISOString();
  flow.stage_history.push({ from: prevStage, to: stage, action: 'advance', username: 'agent', timestamp: new Date().toISOString() });
  if (stage === 'receiving') flow.status = 'paused';
  if (stage === 'close') flow.status = 'completed';
  return { ...flow };
}

// ============================================================================
// SSE Emitter
// ============================================================================

type TokenCallback = (token: AgentToken) => void;

async function emit(cb: TokenCallback, type: AgentTokenType, data?: Record<string, unknown>) {
  await delay(30 + Math.random() * 60);
  cb({ type, ts: Date.now(), data });
}

async function emitText(cb: TokenCallback, text: string) {
  const words = text.split(' ');
  for (let i = 0; i < words.length; i++) {
    const chunk = (i === 0 ? '' : ' ') + words[i];
    await emit(cb, AgentTokenType.TextDelta, { text: chunk });
  }
  await emit(cb, AgentTokenType.TextDone, {});
}

async function emitToolCall(cb: TokenCallback, toolName: string, args: Record<string, unknown>, result: unknown) {
  const toolId = `tool-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  await emit(cb, AgentTokenType.ToolStart, { tool_id: toolId, tool_name: toolName });
  await delay(200 + Math.random() * 300);
  await emit(cb, AgentTokenType.ToolDone, { tool_id: toolId, tool_name: toolName, args: JSON.stringify(args), result: JSON.stringify(result) });
  return toolId;
}

function delay(ms: number) {
  return new Promise(r => setTimeout(r, ms));
}

/** Round to 2 decimal places (safe for sub-cent prices like 0.12) */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Mock address book
const MOCK_ADDRESSES = [
  { id: 'addr-hq', label: '雲騰電通 總公司', type: 'company' as const, address: '新北市中和區中正路866號11樓之1', contact: '王小明', phone: '02-2226-XXXX' },
  { id: 'addr-f1', label: '中和廠', type: 'factory' as const, address: '新北市中和區建一路166號3樓', contact: '李大華', phone: '02-2228-XXXX' },
  { id: 'addr-f2', label: '土城廠', type: 'factory' as const, address: '新北市土城區中央路四段30號', contact: '張美玲', phone: '02-2260-XXXX' },
];

// Mock payment terms
const MOCK_PAYMENT_TERMS = [
  { id: 'net30', label: '月結30天', label_en: 'Net 30', description: 'Invoice + 30 days' },
  { id: 'net60', label: '月結60天', label_en: 'Net 60', description: 'Invoice + 60 days' },
  { id: 'net90', label: '月結90天', label_en: 'Net 90', description: 'Invoice + 90 days' },
  { id: 'cod', label: '貨到付款', label_en: 'COD', description: 'Cash on delivery' },
  { id: 'prepaid', label: '預付', label_en: 'Prepaid', description: 'Payment before shipment' },
  { id: 'tt-advance', label: 'T/T 預付30%', label_en: 'T/T 30% advance', description: '30% upfront, 70% before shipment' },
];

/** Generate mock products for a newly created vendor */
function seedVendorProducts(vendorCode: string, vendorName: string) {
  if (PRODUCTS.some(p => p.vendor_code === vendorCode)) return; // already has products
  const templates: Omit<MockProduct, 'vendor_code' | 'vendor_name'>[] = [
    { part_code: `${vendorCode}-P01`, description: 'Custom Component A', specification: 'General purpose, SMD', last_purchase_price: 25.00, currency: 'TWD', lead_time_days: 14, moq: 100 },
    { part_code: `${vendorCode}-P02`, description: 'Custom Module B', specification: 'Module, DIP package', last_purchase_price: 120.00, currency: 'TWD', lead_time_days: 21, moq: 50 },
    { part_code: `${vendorCode}-P03`, description: 'Cable Assembly C', specification: 'Cable, 30cm, custom', last_purchase_price: 65.00, currency: 'TWD', lead_time_days: 14, moq: 200 },
  ];
  for (const t of templates) {
    PRODUCTS.push({ ...t, vendor_code: vendorCode, vendor_name: vendorName });
  }
}

/** Go back one stage (stages 1-5 only) */
function goBackMockFlow(flowId: string): PurchaseFlow | null {
  const flow = mockFlows.get(flowId);
  if (!flow) return null;
  const idx = STAGE_ORDER.indexOf(flow.current_stage);
  if (idx <= 0 || idx > 4) return null; // can only go back during stages 1-5
  const prevStage = STAGE_ORDER[idx - 1];
  flow.stage_history.push({ from: flow.current_stage, to: prevStage, action: 'back', username: 'agent', timestamp: new Date().toISOString() });
  flow.current_stage = prevStage;
  flow.updated_at = new Date().toISOString();
  return { ...flow };
}

// ============================================================================
// Stage Handlers
// ============================================================================

type StageHandler = (flowId: string, message: string, cb: TokenCallback) => Promise<void>;

const stageHandlers: Record<PurchaseStage, StageHandler> = {

  async vendor_select(flowId, message, cb) {
    if (!message || message.includes('vendor list') || message.includes('Show me')) {
      await emitToolCall(cb, 'psi_list_vendors', { search: '' }, { vendors: VENDORS, total: VENDORS.length });
      await emitText(cb, 'Here are the available vendors. Select one to proceed, or I can create a new vendor for you.\n\n[choices: "Create new vendor"]');
    } else if (message.includes('new vendor') || message.toLowerCase().includes('create new')) {
      const toolId = `tool-${Date.now()}-form`;
      await emit(cb, AgentTokenType.ToolStart, { tool_id: toolId, tool_name: 'psi_create_vendor_form' });
      await emit(cb, AgentTokenType.ToolDone, { tool_id: toolId, tool_name: 'psi_create_vendor_form', result: JSON.stringify({ type: 'form' }) });
      await emitText(cb, 'Fill in the vendor details below and click **Create Vendor**.');
    } else if (message.toLowerCase().startsWith('create vendor:')) {
      const vendorName = message.replace(/^create vendor:\s*/i, '').split(',')[0].trim();
      const newVendor: MockVendor = {
        vendor_code: `V-${String(VENDORS.length + 100).padStart(4, '0')}`,
        name: vendorName,
        tax_id: message.match(/Tax ID:\s*(\d+)/)?.[1] || '',
        contact: message.match(/Contact:\s*([^,]+)/)?.[1] || '',
        phone: message.match(/Phone:\s*([^,]+)/)?.[1] || '',
        email: message.match(/Email:\s*([^,]+)/)?.[1] || '',
      };
      VENDORS.push(newVendor);
      seedVendorProducts(newVendor.vendor_code, newVendor.name);
      await emitToolCall(cb, 'psi_create_vendor', { name: vendorName }, { ...newVendor, status: 'created' });
      const flow = mockFlows.get(flowId)!;
      flow.vendor_code = newVendor.vendor_code;
      flow.stage_data = { ...flow.stage_data, vendor: { code: newVendor.vendor_code, name: newVendor.name }, vendor_code: newVendor.vendor_code, vendor_name: newVendor.name };
      await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId }, { stage: 'item_select' });
      advanceMockFlow(flowId, 'item_select', { vendor_code: newVendor.vendor_code, vendor_name: newVendor.name });
      await emitText(cb, `Vendor **${vendorName}** (${newVendor.vendor_code}) created and selected. What items do you need to purchase?`);
    } else {
      const vendorCodeMatch = message.match(/V-\d+/);
      const match = vendorCodeMatch
        ? VENDORS.find(v => v.vendor_code === vendorCodeMatch[0])
        : VENDORS.find(v => message.toLowerCase().includes(v.name.toLowerCase()));
      if (match) {
        await emitToolCall(cb, 'psi_list_vendors', { search: match.name }, { vendors: [match], total: 1 });
        const flow = mockFlows.get(flowId)!;
        flow.vendor_code = match.vendor_code;
        flow.stage_data = { ...flow.stage_data, vendor: { code: match.vendor_code, name: match.name }, vendor_code: match.vendor_code, vendor_name: match.name };
        await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId, stage_data: { vendor_code: match.vendor_code } }, { stage: 'item_select' });
        advanceMockFlow(flowId, 'item_select', { vendor_code: match.vendor_code, vendor_name: match.name });
        await emitText(cb, `**${match.name}** selected as vendor. What items do you need to purchase?`);
      } else {
        await emitToolCall(cb, 'psi_list_vendors', { search: message }, { vendors: VENDORS, total: VENDORS.length });
        await emitText(cb, 'I found these vendors. Select one or create a new vendor.\n\n[choices: "Create new vendor"]');
      }
    }
  },

  async item_select(flowId, message, cb) {
    const flow = mockFlows.get(flowId)!;
    const vendorCode = (flow.stage_data as Record<string, unknown>)?.vendor_code as string || 'V-0023';
    const vendorProducts = PRODUCTS.filter(p => p.vendor_code === vendorCode);

    // Accumulate items in stage_data
    const existingItems = (flow.stage_data.items as Array<Record<string, unknown>>) || [];

    const lc = message.toLowerCase();
    if (lc.includes('done') || lc.includes("that's all") || lc.includes('完畢') || lc === "that's all the items i need") {
      // Use accumulated items, or default to vendor products
      let items = existingItems;
      if (items.length === 0) {
        // Default: pick up to 2 vendor products with sensible quantities
        items = vendorProducts.slice(0, 2).map((p, i) => ({
          line_no: i + 1, part_code: p.part_code, product_name: p.description, specification: p.specification,
          quantity: p.moq || (i === 0 ? 500 : 1000), unit_price: p.last_purchase_price, currency: p.currency,
        }));
      }
      if (items.length === 0) {
        await emitText(cb, 'You haven\'t added any items yet. Please search for parts or add items first.\n\n[choices: "Search parts" | "Browse parts"]');
        return;
      }
      flow.stage_data = { ...flow.stage_data, items };
      await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId, stage_data: { items } }, { stage: 'price_confirm' });
      advanceMockFlow(flowId, 'price_confirm', { items });
      await emitText(cb, 'Items confirmed. Let me check if there are existing quotations for these prices...');

    } else if (lc.includes('browse parts') || lc.includes('new item') || (lc.includes('add') && (lc.includes('part') || lc.includes('manually')))) {
      // Open part selection dialog embedded in chat
      const vendorName = (flow.stage_data as Record<string, unknown>)?.vendor_name as string || 'vendor';
      const toolId = `tool-${Date.now()}-partpicker`;
      await emit(cb, AgentTokenType.ToolStart, { tool_id: toolId, tool_name: 'psi_open_part_picker' });
      await emit(cb, AgentTokenType.ToolDone, { tool_id: toolId, tool_name: 'psi_open_part_picker', result: JSON.stringify({ type: 'part_picker', vendor_code: vendorCode }) });
      await emitText(cb, `Select a part from the catalog to add to the **${vendorName}** PO, or search by name/spec.`);

    } else if (lc.startsWith('add ')) {
      // Parse qty from message like "Add C-0042 USB-C Connector, qty: 500"
      const qtyMatch = message.match(/qty:\s*(\d+)/i);
      const parsedQty = qtyMatch ? parseInt(qtyMatch[1], 10) : 0;

      // Match part code
      const partMatch = message.match(/Add\s+([\w-]+)\s+([^,]+)/i);
      if (partMatch) {
        const partCode = partMatch[1];
        const product = PRODUCTS.find(p => p.part_code === partCode);
        if (product) {
          const qty = parsedQty || product.moq || 100;
          const newItem = { line_no: existingItems.length + 1, part_code: partCode, product_name: product.description, specification: product.specification, quantity: qty, unit_price: product.last_purchase_price, currency: product.currency };
          const items = [...existingItems, newItem];
          flow.stage_data = { ...flow.stage_data, items };
          await emitToolCall(cb, 'psi_create_product', { product_name: product.description, quantity: qty }, { status: 'added', part_code: partCode, quantity: qty });
          await emitText(cb, `Added **${product.description}** (${partCode}) × ${qty.toLocaleString()} to the order.\n\nAdd more items or say "done" when finished.\n\n[choices: "Search more parts" | "Browse parts" | "Done adding"]`);
          return;
        }
      }
      // Fallback — treat as a description for a custom item
      const desc = message.replace(/^add\s+/i, '').replace(/,\s*qty:.*/i, '').trim();
      const qty = parsedQty || 100;
      const newItem = { line_no: existingItems.length + 1, part_code: `CUSTOM-${existingItems.length}`, product_name: desc, specification: '', quantity: qty, unit_price: 10, currency: 'TWD' };
      const items = [...existingItems, newItem];
      flow.stage_data = { ...flow.stage_data, items };
      await emitToolCall(cb, 'psi_create_product', { product_name: desc, quantity: qty }, { status: 'added', product_name: desc, quantity: qty });
      await emitText(cb, `Added **${desc}** × ${qty.toLocaleString()} to the order.\n\nAdd more items or say "done".\n\n[choices: "Search more parts" | "Browse parts" | "Done adding"]`);

    } else {
      // Search: show vendor products if any, otherwise show all products
      const searchResults = vendorProducts.length > 0 ? vendorProducts : PRODUCTS;
      await emitToolCall(cb, 'psi_search_products', { query: message, vendor_code: vendorCode }, { products: searchResults, total: searchResults.length, query: message });
      const vendorName = (flow.stage_data as Record<string, unknown>)?.vendor_name as string || 'vendor';
      const noVendorMsg = vendorProducts.length === 0 ? '\n\nNo parts found specifically for this vendor. Showing all available parts from the AVL.' : '';
      await emitText(cb, `Here are the matching products.${noVendorMsg} Select items to add, or browse the part catalog.\n\n[choices: "Add ${vendorName} Part" | "Done adding"]`);
    }
  },

  async price_confirm(flowId, message, cb) {
    const flow = mockFlows.get(flowId)!;
    const stageData = flow.stage_data as Record<string, unknown>;
    const vendorCode = stageData?.vendor_code as string || 'V-0023';
    const vendorName = stageData?.vendor_name as string || 'Molex Inc';
    const items = (stageData?.items as Array<Record<string, unknown>>) || [];
    const lc = message.toLowerCase();

    if (lc.includes('price tier') || lc.includes('tier')) {
      // Show price tiers for current items (simulating VendorPart.price_tiers)
      const tierData = items.map((item, idx) => {
        const price = item.unit_price as number;
        return {
          part_code: item.part_code || `PART-${idx}`,
          product_name: item.product_name,
          specification: item.specification || '',
          current_qty: item.quantity,
          current_price: price,
          currency: item.currency || 'TWD',
          tiers: [
            { min_qty: 1, unit_price: round2(price * 1.15) },
            { min_qty: 100, unit_price: round2(price * 1.05) },
            { min_qty: 500, unit_price: price },
            { min_qty: 1000, unit_price: round2(price * 0.92) },
            { min_qty: 5000, unit_price: round2(price * 0.85) },
          ],
        };
      });
      await emitToolCall(cb, 'psi_get_price_tiers', { vendor_code: vendorCode, part_codes: tierData.map(i => i.part_code) }, { items: tierData, vendor_code: vendorCode, vendor_name: vendorName });
      await emitText(cb, `Showing price tiers from **${vendorName}**. Adjust quantities in the table to see how totals change, then apply to update the draft.`);

    } else if (lc.includes('compare') || lc.includes('other vendor')) {
      // Show AVL — other vendors who supply the same parts
      const altVendors = items.map((item, idx) => ({
        part_code: item.part_code || `PART-${idx}`,
        product_name: item.product_name,
        current_vendor: vendorName,
        current_price: item.unit_price,
        alternatives: [
          { vendor_code: 'V-0045', vendor_name: 'TDK Corporation', unit_price: (item.unit_price as number) * 1.08, lead_time_days: 7, moq: 500, approval_status: 'qualified' },
          { vendor_code: 'V-0067', vendor_name: 'Murata Manufacturing', unit_price: (item.unit_price as number) * 0.95, lead_time_days: 21, moq: 1000, approval_status: 'qualified' },
        ],
      }));
      await emitToolCall(cb, 'psi_list_vendor_parts', { part_codes: altVendors.map(i => i.part_code) }, { avl_comparison: altVendors });
      await emitText(cb, `Other qualified vendors in the AVL for these parts:\n\n${altVendors.map(a => `**${a.product_name}**: ${a.current_vendor} @ ${(a.current_price as number).toFixed(0)} vs ${a.alternatives.map(v => `${v.vendor_name} @ ${v.unit_price.toFixed(0)} (${v.lead_time_days}d lead)`).join(', ')}`).join('\n')}\n\nNote: changing vendor would restart the flow. Adjust quantities or confirm prices to proceed with **${vendorName}**.\n\n[choices: "View price tiers" | "Confirm prices"]`);

    } else if (lc.includes('adjust') || lc.includes('quantity') || lc.includes('qty')) {
      // Let user adjust quantities — show current items
      const itemList = items.map((item, i) => `${i + 1}. **${item.product_name}** — qty: ${(item.quantity as number).toLocaleString()} @ ${(item.unit_price as number).toFixed(0)} ${item.currency || 'TWD'}`).join('\n');
      await emitText(cb, `Current items:\n\n${itemList}\n\nTell me which item to change and the new quantity (e.g., "item 1 qty 1000").`);

    } else if (lc.includes('remove')) {
      if (items.length <= 1) {
        await emitText(cb, 'Only one item on the order — cannot remove it. Go back to item select if you need to start over.\n\n[choices: "← Go back"]');
      } else {
        const itemList = items.map((item, i) => `${i + 1}. **${item.product_name}** — qty: ${(item.quantity as number).toLocaleString()}`).join('\n');
        await emitText(cb, `Which item to remove?\n\n${itemList}\n\nTell me the item number (e.g., "remove item 2").`);
      }

    } else if (/^(remove\s+)?item\s*(\d+)/i.test(lc)) {
      // Remove specific item
      const itemIdx = parseInt(lc.match(/item\s*(\d+)/i)![1], 10) - 1;
      if (itemIdx >= 0 && itemIdx < items.length) {
        const removed = items[itemIdx];
        const newItems = items.filter((_, i) => i !== itemIdx).map((item, i) => ({ ...item, line_no: i + 1 }));
        flow.stage_data = { ...stageData, items: newItems };
        await emitText(cb, `Removed **${removed.product_name}** from the order. ${newItems.length} item(s) remaining.\n\n[choices: "View price tiers" | "Confirm prices"]`);
      } else {
        await emitText(cb, `Invalid item number. You have ${items.length} items.`);
      }

    } else if (/item\s*(\d+)\s+qty\s*(\d+)/i.test(lc)) {
      // Adjust specific item quantity
      const match = lc.match(/item\s*(\d+)\s+qty\s*(\d+)/i)!;
      const itemIdx = parseInt(match[1], 10) - 1;
      const newQty = parseInt(match[2], 10);
      if (itemIdx >= 0 && itemIdx < items.length && newQty > 0) {
        const updatedItems = items.map((item, i) => i === itemIdx ? { ...item, quantity: newQty, amount: newQty * (item.unit_price as number) } : item);
        flow.stage_data = { ...stageData, items: updatedItems };
        await emitText(cb, `Updated **${(items[itemIdx] as Record<string, unknown>).product_name}** quantity to **${newQty.toLocaleString()}**.\n\n[choices: "View price tiers" | "Confirm prices"]`);
      } else {
        await emitText(cb, `Invalid item number or quantity. You have ${items.length} items.`);
      }

    } else if (lc.includes('good') || lc.includes('confirm price') || lc.includes('confirm')) {
      // Prices confirmed — advance to delivery schedule (PO created there)
      await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId }, { stage: 'delivery_schedule' });
      advanceMockFlow(flowId, 'delivery_schedule', {});
      await emitText(cb, 'Prices confirmed. When do you need delivery? Default address is 新北市中和區中正路866號11樓之1.\n\n[choices: "2 weeks" | "1 month" | "Custom date"]');

    } else {
      // Default: show current order summary with actionable options
      const itemList = items.map((item, i) => `${i + 1}. **${item.product_name}** — qty: ${(item.quantity as number).toLocaleString()} × ${(item.unit_price as number).toFixed(0)} = ${((item.quantity as number) * (item.unit_price as number)).toLocaleString()} ${item.currency || 'TWD'}`).join('\n');
      const subtotal = items.reduce((sum, i) => sum + (i.quantity as number) * (i.unit_price as number), 0);
      await emitText(cb, `**${vendorName}** — order review:\n\n${itemList}\n\nSubtotal: **TWD ${subtotal.toLocaleString()}** + 5% tax\n\nYou can view price tiers, compare other vendors, adjust quantities, remove items, or confirm prices to proceed.`);
    }
  },

  async delivery_schedule(flowId, message, cb) {
    const flow = mockFlows.get(flowId)!;
    const stageData = flow.stage_data as Record<string, unknown>;
    const vendorCode = stageData?.vendor_code as string || 'V-0023';
    const items = (stageData?.items as Array<Record<string, unknown>>) || [];
    const lc = message.toLowerCase();

    if (lc.includes('delivery date') || lc.includes('交期')) {
      // Emit calendar block
      await emitToolCall(cb, 'psi_show_calendar', {}, {});
      await emitText(cb, 'Select a delivery date from the calendar.');

    } else if (lc.includes('address') || lc.includes('地址')) {
      // Emit address book block
      await emitToolCall(cb, 'psi_list_addresses', {}, { addresses: MOCK_ADDRESSES });
      await emitText(cb, 'Select a delivery address.');

    } else if (lc.includes('payment') || lc.includes('付款')) {
      // Emit payment terms block
      await emitToolCall(cb, 'psi_list_payment_terms', {}, { terms: MOCK_PAYMENT_TERMS, default_id: 'net30' });
      await emitText(cb, 'Select payment terms.');

    } else if (lc.includes('confirm') || lc.includes('create po') || lc.includes('建立')) {
      // Validate required fields before creating PO
      const delivery = stageData?.expected_delivery as string;
      const address = stageData?.delivery_address as string;
      const terms = stageData?.payment_terms as string;
      const missing: string[] = [];
      if (!delivery) missing.push('delivery date');
      if (!address) missing.push('delivery address');
      if (!terms) missing.push('payment terms');

      if (missing.length > 0) {
        await emitText(cb, `Please set **${missing.join(', ')}** before creating the PO.`);
        return;
      }

      // Create PO with all delivery details
      const poItems = items.map((item) => ({
        product_name: item.product_name,
        specification: item.specification || '',
        quantity: item.quantity,
        unit_price: item.unit_price,
        currency: item.currency || 'TWD',
      }));
      const subtotal = poItems.reduce((sum, i) => sum + (i.quantity as number) * (i.unit_price as number), 0);
      const taxAmount = Math.round(subtotal * 0.05);
      const grandTotal = subtotal + taxAmount;
      const poSummary = { ...PO_SUMMARY, vendor_code: vendorCode, items: poItems, subtotal, tax_amount: taxAmount, grand_total: grandTotal };
      await emitToolCall(cb, 'psi_create_purchase_order', {
        vendor_code: vendorCode, items: poItems,
        expected_delivery: delivery, delivery_address: address, payment_terms: terms,
      }, poSummary);
      flow.po_id = 'po-mock-001';
      flow.stage_data = { ...stageData, po_id: 'po-mock-001', po_number: 'FCR-015-202603-0012', grand_total: grandTotal };
      await emitToolCall(cb, 'psi_update_po_status', { po_id: 'po-mock-001', status: 'approved' }, { status: 'approved' });
      await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId }, { stage: 'iqc_rule' });
      advanceMockFlow(flowId, 'iqc_rule', { po_id: 'po-mock-001', po_number: 'FCR-015-202603-0012', expected_delivery: delivery });
      await emitText(cb, `PO **FCR-015-202603-0012** created for **TWD ${grandTotal.toLocaleString()}**, delivery **${delivery}**, address **${stageData?.delivery_address_label || address}**, payment **${terms}**.\n\nWhat IQC inspection level would you like?\n\n[choices: "Full inspection" | "Sampling" | "Waive IQC"]`);

    } else {
      // Default prompt
      await emitText(cb, 'Set delivery details for this order. Use the buttons to set delivery date, address, and payment terms.');
    }
  },

  async iqc_rule(flowId, message, cb) {
    let iqcType = 'full';
    if (message.toLowerCase().includes('sampling')) iqcType = 'sampling';
    if (message.toLowerCase().includes('waive')) iqcType = 'waived';
    await emitToolCall(cb, 'psi_update_po_status', { po_id: 'po-mock-001', status: 'ordered' }, { status: 'ordered' });
    const flow = mockFlows.get(flowId)!;
    flow.stage_data = { ...flow.stage_data, iqc_type: iqcType };
    await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId }, { stage: 'receiving' });
    advanceMockFlow(flowId, 'receiving', { iqc_type: iqcType });
    await emitText(cb, `IQC set to **${iqcType}** inspection. PO status is now **ordered**.\n\nThe flow is paused until the shipment arrives. When goods arrive, come back and tell me!`);
  },

  async receiving(flowId, message, cb) {
    const flow = mockFlows.get(flowId)!;
    if (flow.status === 'paused') flow.status = 'active';
    if (message.includes('arrived') || message.includes('shipment') || message.includes('received')) {
      await emitToolCall(cb, 'psi_create_inbound', { po_id: 'po-mock-001', items: INBOUND_SUMMARY.items }, INBOUND_SUMMARY);
      await emitToolCall(cb, 'psi_update_iqc', { inbound_id: 'ib-mock-001' }, { status: 'iqc_passed' });
      flow.inbound_ids = ['ib-mock-001'];
      flow.stage_data = { ...flow.stage_data, inbound_ids: ['ib-mock-001'] };
      await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId }, { stage: 'invoice_input' });
      advanceMockFlow(flowId, 'invoice_input', { inbound_ids: ['ib-mock-001'] });
      await emitText(cb, 'All items received and IQC passed! Inbound **GR-202603-0001** recorded.\n\nNow let\'s handle the invoice. How would you like to proceed?\n\n[choices: "Use PO data" | "Enter manually" | "Scan invoice"]');
    } else {
      await emitText(cb, 'Has the shipment arrived? Tell me what was received.\n\n[choices: "Shipment arrived" | "Partial delivery" | "Check PO status"]');
    }
  },

  async invoice_input(flowId, message, cb) {
    if (message.includes('PO data') || message.includes('manually') || message.includes('create')) {
      await emitToolCall(cb, 'psi_create_invoice', { source_po_id: 'po-mock-001', items: INVOICE_SUMMARY.items }, INVOICE_SUMMARY);
      const flow = mockFlows.get(flowId)!;
      flow.invoice_id = 'inv-mock-001';
      flow.stage_data = { ...flow.stage_data, invoice_id: 'inv-mock-001', invoice_number: 'XF0875002' };
      await emitToolCall(cb, 'psi_three_way_match', { po_id: 'po-mock-001' }, MATCH_RESULT);
      await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId }, { stage: 'ap_completion' });
      advanceMockFlow(flowId, 'ap_completion', { invoice_id: 'inv-mock-001' });
      await emitText(cb, 'Invoice **XF0875002** created. Three-way match: **all items matched** ✓\n\nReady to record payment.\n\n[choices: "Bank transfer done" | "Show payment details"]');
    } else {
      await emitText(cb, 'How would you like to enter the invoice?\n\n[choices: "Use PO data" | "Enter manually" | "Scan invoice"]');
    }
  },

  async ap_completion(flowId, message, cb) {
    if (message.includes('transfer') || message.includes('paid') || message.includes('payment')) {
      await emitToolCall(cb, 'psi_update_invoice_status', { invoice_id: 'inv-mock-001', status: 'paid', paid_date: PAID_DATE }, { status: 'paid' });
      const flow = mockFlows.get(flowId)!;
      flow.stage_data = { ...flow.stage_data, paid_date: PAID_DATE };
      await emitToolCall(cb, 'psi_flow_advance_stage', { flow_id: flowId }, { stage: 'close' });
      advanceMockFlow(flowId, 'close', { paid_date: PAID_DATE });
      await emitText(cb, `Payment recorded on **${PAID_DATE}**. Let me generate the flow summary.\n\n[choices: "Show summary" | "Close flow"]`);
    } else {
      await emitText(cb, 'Vendor bank details for **Molex Inc**:\n- Bank: 台北富邦銀行 中和分行\n- Code: 012-5103\n- Account: 510301234567\n\nHas payment been made?\n\n[choices: "Bank transfer done" | "Not yet"]');
    }
  },

  async close(flowId, message, cb) {
    await emitToolCall(cb, 'psi_flow_summary', { flow_id: flowId }, FLOW_CHECKLIST);
    if (message.includes('close') || message.includes('Close')) {
      const flow = mockFlows.get(flowId)!;
      flow.status = 'completed';
      await emitToolCall(cb, 'psi_update_po_status', { po_id: 'po-mock-001', status: 'closed' }, { status: 'closed' });
      await emitText(cb, '**Flow closed** ✓ PO FCR-015-202603-0012 is complete.\n\nAll documents: PO → Inbound GR-202603-0001 → Invoice XF0875002 → Paid.');
    } else {
      await emitText(cb, 'Here\'s the complete flow summary. All checks passed!\n\n[choices: "Close flow" | "Go back to review"]');
    }
  },
};

// ============================================================================
// Public API
// ============================================================================

export async function createFlow(flowButtonId?: string): Promise<PurchaseFlow> {
  await delay(100);
  return createMockFlow(flowButtonId);
}

export async function getFlow(flowId: string): Promise<PurchaseFlow> {
  await delay(50);
  const flow = mockFlows.get(flowId);
  if (!flow) throw new Error('Flow not found');
  return { ...flow };
}

/** Directly update flow items (qty/price) without going through agent chat */
export function updateFlowItems(
  flowId: string,
  updates: { part_code: string; quantity: number; unit_price: number }[],
  removals: string[],
): void {
  const flow = mockFlows.get(flowId);
  console.log('[updateFlowItems] flowId:', flowId, 'found:', !!flow);
  if (!flow) return;
  const sd = flow.stage_data as Record<string, unknown>;
  let items = (sd.items as Array<Record<string, unknown>>) || [];
  console.log('[updateFlowItems] items BEFORE:', items.map(i => ({ part_code: i.part_code, quantity: i.quantity, unit_price: i.unit_price })));

  // Apply removals
  if (removals.length > 0) {
    items = items.filter(i => !removals.includes(i.part_code as string));
  }

  // Apply qty/price updates
  for (const upd of updates) {
    const matched = items.find(i => i.part_code === upd.part_code);
    console.log('[updateFlowItems] matching', upd.part_code, '→ found:', !!matched, matched ? `(current: ${matched.part_code})` : '');
    items = items.map(i =>
      i.part_code === upd.part_code
        ? { ...i, quantity: upd.quantity, unit_price: upd.unit_price }
        : i,
    );
  }

  // Renumber
  items = items.map((item, i) => ({ ...item, line_no: i + 1 }));
  flow.stage_data = { ...sd, items };
  flow.updated_at = new Date().toISOString();
  console.log('[updateFlowItems] items AFTER:', items.map(i => ({ part_code: i.part_code, quantity: i.quantity, unit_price: i.unit_price })));
}

/** Add a new item to the flow's items list */
export function addFlowItem(
  flowId: string,
  item: { part_code: string; product_name: string; specification: string; quantity: number; unit_price: number; currency: string },
): void {
  const flow = mockFlows.get(flowId);
  if (!flow) return;
  const sd = flow.stage_data as Record<string, unknown>;
  const items = (sd.items as Array<Record<string, unknown>>) || [];
  // Check for duplicate part_code — update qty instead of adding duplicate
  const existing = items.find(i => i.part_code === item.part_code);
  if (existing) {
    existing.quantity = (existing.quantity as number) + item.quantity;
  } else {
    items.push({ ...item, line_no: items.length + 1 });
  }
  flow.stage_data = { ...sd, items };
  flow.updated_at = new Date().toISOString();
}

/** Directly update flow-level fields (delivery date, address, payment terms) */
export function updateFlowFields(flowId: string, fields: Record<string, unknown>): void {
  const flow = mockFlows.get(flowId);
  if (!flow) return;
  flow.stage_data = { ...flow.stage_data, ...fields };
  flow.updated_at = new Date().toISOString();
}

export async function listFlows(): Promise<FlowListResponse> {
  await delay(50);
  const flows = Array.from(mockFlows.values()).sort((a, b) =>
    new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );
  return { flows, total: flows.length, limit: 20, offset: 0 };
}

export async function sendChatMessage(
  flowId: string,
  message: string,
  onToken: (token: AgentToken) => void,
  _signal?: AbortSignal,
): Promise<void> {
  const flow = mockFlows.get(flowId);
  if (!flow) throw new Error('Flow not found');

  // Handle "go back" universally for stages 2-5
  if (message.toLowerCase().includes('go back') || message.toLowerCase().includes('previous stage') || message.includes('返回')) {
    const prev = goBackMockFlow(flowId);
    if (prev) {
      await emitToolCall(onToken, 'psi_flow_go_back', { flow_id: flowId }, { stage: prev.current_stage });
      await emitText(onToken, `OK, returned to **${prev.current_stage.replace(/_/g, ' ')}**. What would you like to change?`);
      await emit(onToken, AgentTokenType.TurnComplete, {});
      return;
    }
  }

  const handler = stageHandlers[flow.current_stage];
  if (handler) {
    await handler(flowId, message, onToken);
  } else {
    await emitText(onToken, 'This stage is not yet implemented in the mock.');
  }
  await emit(onToken, AgentTokenType.TurnComplete, {});
}

export async function getFlowSummary(flowId: string): Promise<Record<string, unknown>> {
  const flow = mockFlows.get(flowId);
  if (!flow) throw new Error('Flow not found');
  return flow.stage_data;
}
